import { z } from 'zod';
import { optionalE164PhoneSchema } from '@court-sync/shared';

/** When booking for myself: use customer names (pre-filled), require dateOfBirth and gender. */
export const studentFormSchema = z
  .object({
    bookingFor: z.enum(['myself', 'child']),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    dateOfBirth: z.string().optional(),
    gender: z.enum(['male', 'female', 'other']).optional(),
    skillLevel: z.preprocess(
      (val) => (val === '' ? undefined : val),
      z.enum(['beginner', 'intermediate', 'advanced']).optional()
    ),
    medicalNotes: z.string().optional(),
    customerFirstName: z.string().min(1, 'Your first name is required'),
    customerLastName: z.string().min(1, 'Your last name is required'),
    customerEmail: z.string().email('Invalid email address').min(1, 'Your email is required'),
    customerPhone: optionalE164PhoneSchema,
    emergencyContact: z
      .object({
        name: z.string().optional(),
        phone: optionalE164PhoneSchema,
        relationship: z.string().optional(),
      })
      .optional(),
    useCustomerAsEmergencyContact: z.boolean().optional(),
    smsOptIn: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.bookingFor === 'child') {
      if (!data.firstName?.trim()) {
        ctx.addIssue({ path: ['firstName'], message: 'First name is required', code: 'custom' });
      }
      if (!data.lastName?.trim()) {
        ctx.addIssue({ path: ['lastName'], message: 'Last name is required', code: 'custom' });
      }
      if (!data.dateOfBirth?.trim()) {
        ctx.addIssue({ path: ['dateOfBirth'], message: 'Date of birth is required', code: 'custom' });
      }
      if (!data.gender) {
        ctx.addIssue({ path: ['gender'], message: 'Gender is required', code: 'custom' });
      }
    } else if (data.bookingFor === 'myself') {
      // For myself: use customer names, but require dateOfBirth and gender
      if (!data.dateOfBirth?.trim()) {
        ctx.addIssue({ path: ['dateOfBirth'], message: 'Date of birth is required', code: 'custom' });
      }
      if (!data.gender) {
        ctx.addIssue({ path: ['gender'], message: 'Gender is required', code: 'custom' });
      }
    }
    if (data.smsOptIn && !data.customerPhone) {
      ctx.addIssue({
        path: ['customerPhone'],
        message: 'Phone number is required for SMS alerts',
        code: 'custom',
      });
    }
    if (data.bookingFor === 'child' && data.useCustomerAsEmergencyContact !== false) {
      if (!data.emergencyContact?.relationship?.trim()) {
        ctx.addIssue({
          path: ['emergencyContact', 'relationship'],
          message: 'Relationship is required',
          code: 'custom',
        });
      }
    }
  });

export const userFormSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(8, 'Password confirmation is required'),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    phone: optionalE164PhoneSchema,
    termsAccepted: z.boolean().refine((val) => val === true, {
      message: 'You must accept the Terms of Service',
    }),
    privacyAccepted: z.boolean().refine((val) => val === true, {
      message: 'You must accept the Privacy Policy',
    }),
    marketingEmailConsent: z.boolean().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export type StudentFormData = z.infer<typeof studentFormSchema>;
export type UserFormData = z.infer<typeof userFormSchema>;

export function buildEmergencyContactFromStudentForm(
  data: StudentFormData
): { name: string; phone: string; relationship: string } | undefined {
  if (data.bookingFor === 'child' && data.useCustomerAsEmergencyContact !== false) {
    const name = `${data.customerFirstName ?? ''} ${data.customerLastName ?? ''}`.trim();
    if (!name) return undefined;
    return {
      name,
      phone: data.customerPhone ?? '',
      relationship: data.emergencyContact?.relationship?.trim() || 'Parent/Guardian',
    };
  }

  if (!data.emergencyContact?.name?.trim()) return undefined;
  return {
    name: data.emergencyContact.name,
    phone: data.emergencyContact.phone ?? '',
    relationship: data.emergencyContact.relationship ?? '',
  };
}
